import React, { cloneElement } from 'react';
import { useGridNavigation } from './use-grid-navigation';

/**
 * @typedef {Object} GridItemsNavigatorProps
 * @property {number} [columns] - Number of columns in the grid (if not provided, calculated dynamically)
 * @property {boolean} [dynamicColumns=false] - Whether to calculate columns dynamically based on container width
 * @property {number} [itemWidth] - Width of each item in pixels (used for dynamic column calculation)
 * @property {boolean} [loop=true] - Whether navigation should loop around
 * @property {boolean} [disabled=false] - Disables keyboard navigation
 * @property {Function} [onItemActivate] - Callback when an item is activated (Enter/Space)
 * @property {Function} [onItemFocus] - Callback when an item receives focus
 * @property {number} [jumpRowCount=3] - Number of rows to jump on PageUp/PageDown
 * @property {React.ReactNode} children - Grid items to navigate
 */

/**
 * GridItemsNavigator - A component for keyboard navigation through grid items
 * @param {GridItemsNavigatorProps & React.HTMLProps<HTMLDivElement>} props
 */
export function GridItemsNavigator({
  children,
  columns = null,
  dynamicColumns = false,
  itemWidth = null,
  loop = true,
  disabled = false,
  onItemActivate = null,
  onItemFocus = null,
  jumpRowCount = 3,
  className,
  ...props
}) {
  const { items, getItemProps, getContainerProps } = useGridNavigation({
    children,
    columns,
    dynamicColumns,
    itemWidth,
    loop,
    disabled,
    onItemActivate,
    onItemFocus,
    jumpRowCount,
  });

  // Clone each child with navigation props
  const enhancedChildren = items.map((child, index) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const itemProps = getItemProps(index);

    // In React 19, ref is now a regular prop, so we need to preserve it explicitly
    return cloneElement(child, {
      ...itemProps,
      key: child.key || index,
      ref: child.props.ref,
    });
  });

  return (
    <div {...props} {...getContainerProps()} className={className}>
      {enhancedChildren}
    </div>
  );
}
